/**
 * MinimalCover — monograph restraint: a single mark, one accent rule, the
 * title, a few lines of metadata, and vast whitespace. No verdict ribbon,
 * no decorative chrome.
 *
 * Used by sovereign / research seed templates and the per-theme default for
 * the monograph preset. Designed to read as a "report cover" without
 * competing with the document body for visual attention — appropriate for
 * non-specialist or regulated audiences who expect understated typography
 * over data-density.
 *
 * Refined (report-cover pack), NOT renamed: the circular brand mark and the
 * short accent rule are theme-aware (`theme.primaryColor`). Every prior
 * testid + the single-Power-chip suppression behaviour are preserved, so the
 * existing TCP-4 chip-wiring + axe tests stay green.
 */
import React from 'react';
import { PreparedForLine } from '../../PreparedForLine';
import { resolveCoverChipsForVariant } from '../../../../lib/reportRendering/coverKpiChips';
import { CoverChipField } from './CoverChipField';
import type { CoverProps } from './types';
import { CoverCustomerLogo, customerLogoImgClass } from './CoverCustomerLogo';
import { DEFAULT_PRIMARY_COVER_LOGO_URL } from '../../../../lib/reportBranding';
import { roleColor } from '../../../../lib/reportRoleColors';
import { reportTypographySizes, reportHeadingInk } from '../../../../lib/reportSectionHeading';
import { resolveCoverTitle } from '../../../../lib/reportRendering/journalCoverData';
import { ReportPlatformCredit, platformCreditToneForTheme } from '../ReportPlatformCredit';

export const MinimalCover: React.FC<CoverProps> = ({
  report, theme, branding, isAdmin,
  pickerOpen, onTogglePicker, onSelectArchetype,
  disclaimer,
}) => {
  // L-id-leak — never fall back to the raw Firestore `report.id`.
  const fallbackSiteWordmark = report.siteName?.trim() || 'Unnamed site';
  const effectiveBranding = branding ?? {
    audienceMode: 'internal',
    siteWordmark: fallbackSiteWordmark,
    primaryLogoUrl: theme.logoUrl ?? DEFAULT_PRIMARY_COVER_LOGO_URL,
    secondaryLogoUrl: null,
    primaryLogoAlt: theme.companyName ?? 'Primary logo',
    secondaryLogoAlt: 'Customer logo',
  };
  // P3 — title from the ONE shared resolver (config-label source of truth).
  const titleLabel = resolveCoverTitle(report);

  // report-cover consistency: derive the title size from the theme's
  // headingScale rather than a fixed `text-4xl`. The editorial h2
  // baseline is 1.5rem; minimal's display title read at text-4xl
  // (2.25rem) ≈ 1.5× the h2, so we keep that 1.5× display ratio while
  // letting technical/compact presets scale it in step with section
  // headings.
  const titleFontSize = `calc(${reportTypographySizes(theme).h2} * 1.5)`;

  return (
    <div className="relative space-y-12 max-w-2xl pb-14" data-testid="cover-minimal" data-cover-variant="minimal">
      {/* Single brand mark — a ringed dot in the theme accent colour. Pure
          decoration (aria-hidden); anchors the page without competing with
          the title. */}
      <div
        aria-hidden="true"
        data-testid="cover-minimal-mark"
        style={{
          width: 30,
          height: 30,
          borderRadius: '50%',
          border: `1.5px solid ${theme.primaryColor}`,
          position: 'relative',
        }}
      >
        <span
          style={{
            content: '""',
            position: 'absolute',
            inset: 9,
            borderRadius: '50%',
            background: theme.primaryColor,
            display: 'block',
          }}
        />
      </div>
      {(effectiveBranding.primaryLogoUrl || effectiveBranding.secondaryLogoUrl) && (
        <div className="flex items-center gap-3" data-testid="cover-minimal-logos" data-audience-mode={effectiveBranding.audienceMode}>
          {effectiveBranding.primaryLogoUrl && (
            <img
              src={effectiveBranding.primaryLogoUrl}
              alt={effectiveBranding.primaryLogoAlt}
              className="h-6 max-w-[104px] object-contain"
              data-testid="cover-minimal-logo-primary"
            />
          )}
          {effectiveBranding.audienceMode === 'customer' && effectiveBranding.secondaryLogoUrl && (
            <>
              <span className="mx-2 h-5 w-px" style={{ backgroundColor: roleColor(theme, 'hair') }} aria-hidden="true" />
              <CoverCustomerLogo
                src={effectiveBranding.secondaryLogoUrl}
                alt={effectiveBranding.secondaryLogoAlt}
                tone="on-light"
                className={customerLogoImgClass('on-light', 'h-6 max-w-[104px]')}
                data-testid="cover-minimal-logo-secondary"
              />
            </>
          )}
        </div>
      )}
      <div
        className="text-xs font-bold uppercase tracking-[0.4em]"
        style={{ color: roleColor(theme, 'muted') }}
        data-testid="cover-site-name-header"
      >
        {effectiveBranding.siteWordmark}
      </div>
      <div className="space-y-4">
        <div className="text-sm" style={{ color: roleColor(theme, 'body') }}>{report.address}</div>
        <h1
          className="font-light leading-tight tracking-tight"
          style={{
            fontFamily: theme.displayFont || theme.headingFont,
            color: reportHeadingInk(theme),
            fontSize: titleFontSize,
          }}
        >
          {titleLabel}
        </h1>
        {/* One short accent rule in the theme accent colour. */}
        <div
          aria-hidden="true"
          data-testid="cover-minimal-accent-rule"
          style={{ width: 54, height: 2, background: theme.primaryColor }}
        />
        <PreparedForLine
          report={report}
          isAdmin={isAdmin}
          pickerOpen={pickerOpen}
          onTogglePicker={onTogglePicker}
          onSelectArchetype={onSelectArchetype}
        />
        {/* TCP-4 (row 0.99233) — single quiet Power chip. Minimal's
             design language ("quiet typographic cover") rules out a
             full chip strip, but the headline Power number anchors
             the page when set. Suppressed entirely when undecided so
             a fresh template doesn't surface an em-dash on a cover
             that's otherwise typography-only. */}
        {(() => {
          const chips = resolveCoverChipsForVariant(report, 'minimal');
          const visible = chips.filter(c => c.value !== null);
          if (visible.length === 0) return null;
          return (
            <div className="flex items-baseline gap-6 flex-wrap pt-2" data-testid="cover-minimal-kpis">
              {visible.map(chip => (
                <div key={chip.id} data-testid={`cover-minimal-chip-${chip.id}`}>
                  <CoverChipField
                    chip={chip}
                    report={report}
                    theme={theme}
                    testIdSuffix={`minimal-${chip.id}`}
                    labelClassName="text-[10px] font-bold uppercase tracking-[0.3em]"
                    valueClassName="text-base font-light tabular-nums"
                  />
                </div>
              ))}
            </div>
          );
        })()}
      </div>
      {/* GATEWAY POWER platform credit — left-aligned just BELOW the cover box
          (absolute, top: 100% + 3px): it lands in the cover shell's bottom
          padding on screen and in the empty rest of the page-tall #cover in
          print, so it adds no height and never meets the bottom-right
          disclaimer, whose width-dependent wrap collided with a bottom-left
          credit on phones. Rendered BEFORE the disclaimer with its margin
          zeroed: Tailwind v4 `space-y-*` margins every child but the last, and
          the disclaimer must stay the last child to keep its exact position. */}
      <ReportPlatformCredit
        tone={platformCreditToneForTheme(theme)}
        color={roleColor(theme, 'muted')}
        className="absolute left-0 top-[calc(100%_+_3px)]"
        style={{ marginBlock: 0 }}
        testId="cover-minimal-platform-credit"
      />

      {/* CONFIDENTIAL DISCLAIMER — bottom-right footnote (COVER-CONTENT BATCH 4),
          rendered at 80% of the prior 10px size (→ 8px). */}
      <div
        className="absolute bottom-0 right-0 text-right text-[8px] leading-relaxed max-w-md"
        style={{ color: roleColor(theme, 'muted') }}
        data-testid="cover-minimal-disclaimer"
      >
        {disclaimer}
      </div>
    </div>
  );
};
